import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { act } from "react";
import sanitizeFileName from "helpers/sanitizeFileName";
import { mergeExistingFields } from "helpers/mergeExistingFields";
import { ApiErrorPayload } from "types/ApiErrorPayload";

export enum NotificationEmailTypes {
  registration = "registration",
  resetPassword = "resetPassword",
  verifyEmail = "verifyEmail",
  all = "all"
}

export interface EmailTemplateItem {
  id: number;
  title: string;
  type: NotificationEmailTypes;
  key: string;
  headerImageRef: string;
  content: string;
  isActive: number;
};

export interface CreateEmailTemplateItemPayload extends Omit<EmailTemplateItem, 'id' | 'headerImageRef'> {
  header?: File;
}

export interface UpdateEmailTemplateItemPayload extends Partial<Omit<EmailTemplateItem, 'headerImageRef'>> {
  header?: File;
}

interface GetListPayload {
  limit: number;
  offset: number;
  type: string[];
  title?: string;
  key?: string;
  isActive?: number;
  order: {
    title?: string;
    isActive?: string;
  };
}

interface EmailListReposnse {
  items: Array<Omit<EmailTemplateItem, 'headerImageRef' | 'content'>>;
  totalItems: number;
  totalPages: number;
}

const getEmailTemplateList = createAsyncThunk(
  "emails/list",
  async (data: GetListPayload) => {
    try {
      const result = await instance.post(`${apiRoutes.emailTemplateList}`, {
        ...data
      });
      return result.data as EmailListReposnse;
    } catch (error) {
      return undefined;
    }
  }
);

const deleteEmailTemplateItem = createAsyncThunk(
  'emails/delete',
  async (id: number) => {
    try {
      await instance.delete(`${apiRoutes.emailTemplateItemDelete}/${id}`);

      return true;
    } catch (error) {
      return undefined;
    }
  }
);

const createEmailTemplateItem = createAsyncThunk(
  'emails/create',
  async (payload: CreateEmailTemplateItemPayload) => {
    try {
      const formData = new FormData();

      Object.keys(payload).forEach((key) => {
        const k = key as keyof CreateEmailTemplateItemPayload;

       if (k === "header" && payload.header instanceof File) {
         const safeName = sanitizeFileName(payload.header.name);

         formData.append(
           key,
           new File([payload.header], safeName, { type: payload.header.type })
         );
         return;
       }

        formData.append(key, `${payload[k]}`);
      });

      const result = await instance.post(
        `${apiRoutes.emailTemplateItemCreate}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        },
      );

      return result.data as EmailTemplateItem;
    } catch (error) {
      return undefined;
    }
  }
);

const updateEmailTemplateItem = createAsyncThunk(
  'emails/update',
  async (payload: UpdateEmailTemplateItemPayload) => {
    try {
      const { id, ...rest } = payload;

      const formData = new FormData();

      Object.keys(rest).forEach((k) => {
        const key = k as keyof UpdateEmailTemplateItemPayload;

        if (key === 'header') {
          formData.append(key, rest.header as File, rest.header?.name);
          return;
        }

        formData.append(key, `${payload[key]}`);
      })

      const result = await instance.put(
        `${apiRoutes.emailTemplateItemUpdate}/${id}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" }
        },
      );

      return result.data as EmailTemplateItem;
    } catch (error) {
      return undefined;
    }
  }
)

const getEmailTemplateTypes = createAsyncThunk(
  'emails/types',
  async () => {
    try {
      const result = await instance.get(`${apiRoutes.emailTemplateTypes}?limit=20&offset=0`);

      return result.data as { types: string[] };
    } catch (error) {
      return undefined;
    }
  }
);

const getEmailTemplateItem = createAsyncThunk(
  'emails/getItem',
  async (id: string) => {
    try {
      const result = await instance.get(`${apiRoutes.emailTemplateItem}/${id}`);

      return result.data as EmailTemplateItem;
    } catch (error) {
      return undefined;
    }
  }
);

const getEmailTemplateTranslation = createAsyncThunk(
  'emails/get-translations',
  async ({ id, locale }: { id: string; locale: string }) => {
    try {
      const result = await instance.get(
        `${apiRoutes.emailTemplateTranslationsGet}/${id}?locale=${locale}`
      );
      const translation = result?.data?.data ?? result?.data ?? result;
      
      return {
        locale,
        ...translation,
      } as EmailTranslationPayload;
    } catch (error) {
      throw error;
    }
  }
);

const setEmailTemplateTranslation = createAsyncThunk<
  EmailTranslationPayload,
  { id: string; payload: EmailTranslationPayload },
  { rejectValue: ApiErrorPayload }
>(
  'emails/set-translations',
  async (
    { id, payload }: { id: string; payload: EmailTranslationPayload },
    { rejectWithValue },
  ) => {
    try {
      await instance.put(
        `${apiRoutes.emailTemplateTranslationsSet}/${id}`,
        payload,
      );
      return payload;
    } catch (error: any) {
      const responseData = error?.response?.data ?? error?.data;
      const apiError = responseData?.error ?? responseData;

      if (apiError?.code) {
        return rejectWithValue(apiError as ApiErrorPayload);
      }

      if (Array.isArray(responseData?.errors)) {
        return rejectWithValue({
          code: "BusinessError",
          fieldErrors: [],
          globalErrors: responseData.errors.map((item: { message?: string }) => ({
            code: item.message ?? "unknownError",
          })),
        });
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  }
)


export interface EmailTranslationPayload {
  locale: string;
  title: string;
  isActive: number | boolean;
  content: string;
}

interface EmailTranslationState {
  [locale: string]: EmailTranslationPayload;
}

interface EmailState {
  list: Array<Omit<EmailTemplateItem, 'headerImageRef' | 'content'>>;
  isListFetched: boolean;
  isItemFetched: boolean;
  activePage: number;
  totalItems: number;
  totalPages: number;
  selectedItems: number[];
  types: string[];
  translations: EmailTranslationState;
  draftItem?: Omit<EmailTemplateItem, 'id'> & { id?: number };
  isItemFetching: boolean;
  processItemId?: EmailTemplateItem['id'];
}

const initialState: EmailState = {
  list: [],
  isListFetched: false,
  isItemFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  types: [],
  translations: {},
  isItemFetching: false,
}


const emailSlice = createSlice({
  name: "emails",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
      state.selectedItems = [];
    },
    handleSelectAll: (state) => {
      state.selectedItems =
        state.selectedItems.length !== state.list.length
          ? state.list.map((item) => item.id)
          : [];
    },
    handleSelect: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems.push(action.payload);
      }
    },
    handleClearDraftItem: (state) => {
      state.draftItem = undefined;
      state.isItemFetched = false;
    },
    handleUpdateFieldDraftItem: (
      state,
      payload: PayloadAction<Partial<EmailTemplateItem>>,
    ) => {
      state.draftItem = {
        ...(state.draftItem ?? ({} as EmailTemplateItem)),
        ...payload.payload,
      };
    },
    handleUpdateFieldTranslation: (
      state,
      payload: PayloadAction<Partial<EmailTranslationPayload>>,
    ) => {
      const { locale, ...rest } = payload.payload;
      state.translations[locale as string] = {
        ...state.translations[locale as string],
        ...rest,
      };
    },
    clearEmailTranslation: (state, action: PayloadAction<string>) => {
      delete state.translations[action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getEmailTemplateList.fulfilled, (state, action) => {
      state.isListFetched = true;
      state.list = action.payload?.items
        ? action.payload.items.map((itm) => ({
            ...itm,
            isActive: itm.isActive ? 1 : 0,
          }))
        : [];
      state.totalItems = action.payload?.totalItems ?? 1;
      state.totalPages = action.payload?.totalPages ?? 1;
    }),
      builder.addCase(deleteEmailTemplateItem.pending, (state, active) => {
        state.processItemId = +active.meta.arg || undefined;
      }),
      builder.addCase(deleteEmailTemplateItem.fulfilled, (state) => {
        state.isListFetched = false;
        state.processItemId = undefined;
      }),
      builder.addCase(deleteEmailTemplateItem.rejected, (state) => {
        state.processItemId = undefined;
      }),
      builder.addCase(createEmailTemplateItem.pending, (state) => {
        state.isItemFetching = true;
      }),
      builder.addCase(createEmailTemplateItem.fulfilled, (state) => {
        state.isListFetched = false;
        state.isItemFetching = false;
      }),
      builder.addCase(createEmailTemplateItem.rejected, (state) => {
        state.isListFetched = false;
        state.isItemFetching = false;
      }),
      builder.addCase(updateEmailTemplateItem.pending, (state, action) => {
        state.isItemFetching = true;
        state.processItemId = action.meta.arg.id;
      }),
      builder.addCase(updateEmailTemplateItem.fulfilled, (state, action) => {
        const updatedItem = action.payload;
        if (updatedItem) {
          state.list = state.list.map((item) =>
            item.id === updatedItem.id
              ? mergeExistingFields(item, updatedItem)
              : item,
          );
        }
        state.isItemFetching = false;
        state.processItemId = undefined;
      }),
      builder.addCase(updateEmailTemplateItem.rejected, (state) => {
        state.isItemFetching = false;
        state.processItemId = undefined;
      }),
      builder.addCase(getEmailTemplateTypes.fulfilled, (state, action) => {
        state.types = action.payload?.types ?? [];
      }),
      builder.addCase(getEmailTemplateItem.fulfilled, (state, action) => {
        state.isItemFetched = true;
        state.draftItem = action.payload
          ? {
              ...action.payload,
              isActive: action.payload.isActive ? 1 : 0,
            }
          : undefined;
      }),
      builder.addCase(
        getEmailTemplateTranslation.fulfilled,
        (state, action) => {
          const { locale } = action.payload;

          state.translations[locale] = action.payload;
        },
      ),
      builder.addCase(setEmailTemplateTranslation.pending, (state) => {
        state.isItemFetching = true;
      }),
      builder.addCase(
        setEmailTemplateTranslation.fulfilled,
        (state, action) => {
          state.isItemFetching = false;
          state.isListFetched = false;
          state.translations[action.payload.locale] = action.payload;
        },
      ),
      builder.addCase(setEmailTemplateTranslation.rejected, (state) => {
        state.isItemFetching = false;
      });
  },
});



const {
  handlePaginate,
  handleSelectAll,
  handleSelect,
  handleClearDraftItem,
  handleUpdateFieldDraftItem,
  handleUpdateFieldTranslation,
  clearEmailTranslation,
} = emailSlice.actions;


export {
  emailSlice,
  getEmailTemplateList,
  handlePaginate,
  handleSelectAll,
  handleSelect,
  deleteEmailTemplateItem,
  createEmailTemplateItem,
  updateEmailTemplateItem,
  getEmailTemplateTypes,
  getEmailTemplateItem,
  handleClearDraftItem,
  handleUpdateFieldDraftItem,
  getEmailTemplateTranslation,
  setEmailTemplateTranslation,
  handleUpdateFieldTranslation,
  clearEmailTranslation
}
